import Element, { ElementType, SpecyficType } from './element';
import { Vector2 } from '@babylonjs/core';
import { placementManager } from '@/models/placementManager';
import IShape, { BaseShape } from './IShape';
import { WallModel } from './wallModel';
import store from '@/store';
import { ProductType } from '@/store/modules/product';

class WallManager {
    private elements!: Element[];

    public initialize(elements: Element[]) {
        this.elements = elements;
    }

    public getWallModels(newElement: Element): WallModel[] {
        const result: WallModel[] = [];

        const slot = newElement.slot;

        const body = this.elements.find((x) => x.type === ElementType.BODY)!;
        const shape = slot.shape.y % 2 ? new Vector2(0, 1) : new Vector2(0, 0);

        const left = new Vector2(slot.position.x - slot.shape.x / 2,
            slot.position.y);
        if (placementManager.canPlace(left, shape, ElementType.WALL) &&
            left.x > 1) {
            const model = this.getWallModel(left, shape.clone(), body, 'y');
            result.push(model);
            result.push(...this.getHorizontalWallModels(model, body));
        }

        const right = new Vector2(slot.position.x + slot.shape.x / 2,
            slot.position.y);
        if (placementManager.canPlace(right, shape.clone(), ElementType.WALL) &&
            right.x < body.shape.x) {
            const model = this.getWallModel(right, shape, body, 'y');
            result.push(model);
            result.push(...this.getHorizontalWallModels(model, body));
        }

        if (newElement.type === ElementType.FRONT) {
            const sh = new Vector2(slot.shape.x, 0);

            const up = new Vector2(slot.position.x,
                slot.position.y + slot.shape.y / 2);
            if (placementManager.canPlace(up, sh, ElementType.WALL) &&
                up.y < body.shape.y) {
                const baseShape = new BaseShape(up, sh);
                const model = this.createModel('x', baseShape);
                const light = this.createLight('x', baseShape);
                const wall = new WallModel(model, light, up, sh);
                result.push(wall);
            }

            const down = new Vector2(slot.position.x,
                slot.position.y - slot.shape.y / 2);
            if (placementManager.canPlace(down, sh, ElementType.WALL) &&
                down.y > 1) {
                const baseShape = new BaseShape(down, sh);
                const model = this.createModel('x', baseShape);
                const light = this.createLight('x', baseShape);
                const wall = new WallModel(model, light, down, sh);
                result.push(wall);
            }
        }

        const wallSupports = this.createWallSupports(result);
        const cellingSupports = this.createCellingSupports(result);
        result.push(...wallSupports);
        result.push(...cellingSupports);
        return result;
    }

    public inUnused(wall: Element): boolean {
        const elementsToTest = this.elements.filter((e) =>
            e.type === ElementType.WALL ||
            e.type === ElementType.FRONT ||
            (
                e.type === ElementType.INSIDE &&
                e.specyficType !== SpecyficType.HANGER
            ),
        );
        const tests: IShape[] = [];
        const corner = wall.corner.clone();
        if (wall.shape.x === 0) { // isVertical
            for (let i = 0.5; i < wall.shape.y; i += 0.5) {
                const testLeft = new BaseShape(
                    new Vector2(corner.x - 0.25, corner.y + i),
                    new Vector2(0, 0),
                );
                const testRight = new BaseShape(
                    new Vector2(corner.x + 0.25, corner.y + i),
                    new Vector2(0, 0),
                );
                tests.push(testLeft);
                tests.push(testRight);
            }
        } else { // isHorizontal
            for (let i = 0.5; i < wall.shape.x; i += 0.5) {
                const testUp = new BaseShape(
                    new Vector2(corner.x + i, corner.y + 0.25),
                    new Vector2(0, 0),
                );
                const testDown = new BaseShape(
                    new Vector2(corner.x + i, corner.y - 0.25),
                    new Vector2(0, 0),
                );
                tests.push(testUp);
                tests.push(testDown);
            }
        }
        return !elementsToTest.some((e) => tests.some((t) => t.inside(e)));
    }

    public canPlaceWallSupports(): boolean {
        const product = store.getters.product;
        return product.depth !== 455 || product.height > 910;
    }

    public createCellingSupports(walls: WallModel[]): WallModel[] {
        if (this.canPlaceWallSupports()) { return []; }
        if (store.getters.product.type === ProductType.DRESSING) { return []; }

        const product = store.getters.product;
        const supports: WallModel[] = [];

        const newVerticalWalls = walls
            .filter((x) =>
                x.isVertical,
            );
        const allVerticalWalls: IShape[] = [];
        allVerticalWalls.push(...newVerticalWalls);
        allVerticalWalls.push(...this.elements
            .filter((x) =>
                x.type === ElementType.WALL &&
                x.shape.x === 0,
            ));

        const shape = product.shape as Vector2;
        for (let i = 0; i <= shape.x - 2; i++) {
            const test = new BaseShape(
                new Vector2(i + 1.5, shape.y),
                new Vector2(2.1, 0),
            );
            const left = new BaseShape(
                new Vector2(test.position.x - (test.shape.x - 0.1) / 2, test.position.y),
                Vector2.Zero(),
            );
            const right = new BaseShape(
                new Vector2(test.position.x + (test.shape.x - 0.1) / 2, test.position.y),
                Vector2.Zero(),
            );
            const colliders = allVerticalWalls.filter((x) =>
                test.collide(x),
            );
            if (
                colliders.every((x) => left.inside(x) || right.inside(x)) &&
                (colliders.length === 2 ||
                    (colliders.length === 1 && (i === 0 || i === shape.x - 2)) ||
                    (colliders.length === 0 && (i === 0 && i === shape.x - 2)))) {
                const support = new WallModel(
                    'listwa_podp.gltf',
                    'listwa_podp_light.jpg',
                    new Vector2(i + 1.5, shape.y + 0.5),
                    new Vector2(2, 0),
                    true,
                );
                if (supports.some((x) => x.collide(test))) { continue; }
                supports.push(support);
            }
        }

        return supports;
    }

    public createWallSupports(walls: WallModel[]): WallModel[] {
        const supports: WallModel[] = [];

        const newVerticalWalls = walls.filter((x) => x.isVertical);
        const allHorizontalWalls: IShape[] = [];
        const existingHorizontalWalls = this.elements
            .filter((x) =>
                x.type === ElementType.WALL &&
                x.shape.y === 0 &&
                x.shape.x === 2,
            );
        const newHorizontalWals = walls
            .filter((x) =>
                !x.isVertical &&
                x.shape.x === 2,
            );
        allHorizontalWalls.push(...existingHorizontalWalls);
        allHorizontalWalls.push(...newHorizontalWals);
        const existingVerticalWalls = this.elements
            .filter((x) =>
                x.type === ElementType.WALL &&
                x.shape.x === 0,
            );

        newVerticalWalls.forEach((vertical) => {
            allHorizontalWalls.forEach((horizontal) => {
                const test = new BaseShape(
                    new Vector2(
                        vertical.position.x,
                        vertical.position.y - vertical.shape.y / 2),
                    Vector2.Zero(),
                );
                const test2 = new BaseShape(
                    new Vector2(
                        vertical.position.x,
                        test.position.y - 0.5,
                    ),
                    Vector2.Zero(),
                );
                if (
                    test.inside(horizontal) &&
                    !existingVerticalWalls.some((x) => test2.inside(x))
                ) {
                    const support = new WallModel(
                        'listwa_podp.gltf',
                        'listwa_podp_light.jpg',
                        horizontal.position,
                        horizontal.shape,
                        true,
                    );
                    supports.push(support);
                }
            });
        });

        return supports;
    }

    private getHorizontalWallModels(verticalWallModel: WallModel, body: Element): WallModel[] {
        const result: WallModel[] = [];

        const model = verticalWallModel;
        const horPos1 = new Vector2(model.position.x,
            model.position.y + model.shape.y / 2);
        const horShape = new Vector2(0, 0);
        if (placementManager.canPlace(horPos1, horShape, ElementType.WALL) &&
            horPos1.y < body.shape.y) {
            const horWall = this.getWallModel(horPos1, horShape.clone(), body, 'x');
            result.push(horWall);
        }

        const horPos2 = new Vector2(model.position.x,
            model.position.y - model.shape.y / 2);
        if (placementManager.canPlace(horPos2, horShape, ElementType.WALL) &&
            horPos2.y > 1) {
            const horWall = this.getWallModel(horPos2, horShape.clone(), body, 'x');
            result.push(horWall);
        }

        return result;
    }

    private getWallModel(position: Vector2, shape: Vector2, body: Element, axis: string): WallModel {
        const q = new BaseShape(position, shape) as any;

        while (placementManager.canPlace(q.position, q.shape, ElementType.WALL) &&
            q.inside(body)) {
            q.position[axis] += 0.25;
            q.shape[axis] += 0.5;
        }

        q.position[axis] -= 0.25;
        q.shape[axis] -= 0.5;

        while (placementManager.canPlace(q.position, q.shape, ElementType.WALL) &&
            q.inside(body)) {
            q.position[axis] -= 0.25;
            q.shape[axis] += 0.5;
        }

        q.position[axis] += 0.25;
        q.shape[axis] -= 0.5;

        if (axis === 'y') {
            this.correctVerticalModel(q);
        }

        const model = this.createModel(axis, q);
        const light = this.createLight(axis, q);

        return new WallModel(model, light, q.position, q.shape);
    }

    private correctVerticalModel(q: IShape) {
        const upperShelf = this.elements
            .find((x) =>
                x.specyficType === SpecyficType.SHELF &&
                x.position.x === q.position.x &&
                x.position.y === q.position.y + q.shape.y / 2);

        const lowerShelf = this.elements
            .find((x) =>
                x.specyficType === SpecyficType.SHELF &&
                x.position.x === q.position.x &&
                x.position.y === q.position.y - q.shape.y / 2);

        if (!Number.isInteger(q.shape.y) &&
            upperShelf &&
            Number.isInteger(upperShelf.position.y)) {
            q.position.y -= 0.25;
            q.shape.y -= 0.5;
        }

        if (!Number.isInteger(q.shape.y) &&
            lowerShelf &&
            Number.isInteger(lowerShelf.position.y)) {
            q.position.y += 0.25;
            q.shape.y -= 0.5;
        }

        if (Number.isInteger(q.shape.y) &&
            upperShelf &&
            Number.isInteger(upperShelf.position.y) &&
            lowerShelf &&
            Number.isInteger(lowerShelf.position.y)) {
            q.position.y += 0.25;
            q.position.y -= 0.25;
            q.shape.y -= 1;
        }

        if (q.shape.y === 0) {
            q.shape.y = 1;
        }
    }

    private createModel(axis: string, element: BaseShape): string {
        const q = element as any;
        if (store.getters.product.type === ProductType.DESK ||
            store.getters.product.type === ProductType.DRESSING) {
            return 'b_ppio.gltf';
        }
        return `p${axis === 'y' ? 'pio' : 'poz'}_${store.getters.product.depth}_${q.shape[axis]}.gltf`;
    }

    private createLight(axis: string, element: BaseShape): string {
        const q = element as any;
        if (store.getters.product.type === ProductType.DESK ||
            store.getters.product.type === ProductType.DRESSING) {
            return 'b_ppio_light.jpg';
        }
        return `p${axis === 'y' ? 'pio' : 'poz'}_${store.getters.product.depth}_${q.shape[axis]}_mesh_light.jpg`;
    }
}

export const wallManager = new WallManager();
